<template>
  <div
    class="ui-tabs"
    :class="[
      direction ? 'ui-tabs--' + direction : '',
      type ? 'ui-tabs--' + type : '',
    ]"
  >
    <div class="ui-tabs__nav-wrapper">
      <div v-if="navTitle" class="ui-tabs__nav-title">
        {{ navTitle }}
      </div>
      <div
        class="ui-tabs__btn ui-tabs__btn-up"
        :class="{lock: activeTabIndex === 0}"
        @click="changeTab('up')"
        :style="{
          rotate: direction === 'horizontal' ? '270deg' : '0deg',
        }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.04176 10.2762L11.4054 16.9429C11.7327 17.2858 12.2674 17.2858 12.5947 16.9429L18.9583 10.2762C19.2806 9.93858 19.2806 9.39485 18.9583 9.05722C18.631 8.71433 18.0963 8.71433 17.769 9.05722L12 15.1009L6.23106 9.05722C5.90376 8.71433 5.36907 8.71433 5.04176 9.05722C4.71948 9.39485 4.71948 9.93858 5.04176 10.2762Z"
          />
        </svg>
      </div>
      <div
        class="ui-tabs__nav-shadow"
        :class="{
          top: activeTabIndex < 2,
          bottom: activeTabIndex > tabs.length - 3,
        }"
      >
        <ul ref="navListRef" class="ui-tabs__nav">
          <li
            v-for="tab in tabs"
            :key="tab.computedId"
            :ref="setItemRef"
            class="btn-text"
            :class="[
              'ui-tabs__tab',
              tab.isDisabled ? 'is-disabled' : '',
              tab.isActive ? 'is-active' : '',
            ]"
            @click="selectWithScroll(tab.computedId)"
          >
            {{ tab.header }}
          </li>
        </ul>
      </div>
      <div
        class="ui-tabs__btn"
        :class="{
          lock: activeTabIndex === tabs.length - 1,
        }"
        :style="{
          rotate: direction === 'horizontal' ? '270deg' : '0deg',
        }"
        @click="changeTab('down')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.04176 10.2762L11.4054 16.9429C11.7327 17.2858 12.2674 17.2858 12.5947 16.9429L18.9583 10.2762C19.2806 9.93858 19.2806 9.39485 18.9583 9.05722C18.631 8.71433 18.0963 8.71433 17.769 9.05722L12 15.1009L6.23106 9.05722C5.90376 8.71433 5.36907 8.71433 5.04176 9.05722C4.71948 9.39485 4.71948 9.93858 5.04176 10.2762Z"
          />
        </svg>
      </div>
    </div>

    <div class="ui-tabs__panel">
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {useTabs} from '~/hooks/ui/useTabs';
import UiSelect from '~/components/Ui/UiSelect.vue';
import {computed, ref} from '#imports';

type Props = {
  direction: String;
  type: String;
  navTitle: String;
};

const props = defineProps<Props>();

const emits = defineEmits(['changed', 'clicked', 'beforeChange']);
const {tabs, selectTab, activeTabId} = useTabs(emits);
const selectModel = ref(activeTabId);
const selectOption = computed(() =>
  tabs.value.map((tab) => ({
    id: tab.computedId,
    value: tab.name,
    text: tab.name,
    selected: false,
  })),
);
const navListRef = ref();
let itemRefs: any = [];
const setItemRef = (el: any) => {
  if (el) {
    itemRefs.push(el);
  }
};

const activeTabIndex = computed(() => {
  return tabs.value.findIndex(
    (tab) => activeTabId.value === tab.computedId,
  );
});

function selectWithScroll(id: string) {
  selectTab(id);
  const tabRef = itemRefs[activeTabIndex.value];
  navListRef.value.scrollTo({
    top: tabRef.offsetTop - navListRef.value.offsetHeight / 2,
    behavior: 'smooth',
  });
}

function changeTab(direction: 'up' | 'down') {
  if (direction === 'up' && activeTabIndex.value > 0) {
    selectTab(tabs.value[activeTabIndex.value - 1].computedId);
    const tabRef = itemRefs[activeTabIndex.value];
    navListRef.value.scrollTo({
      top: tabRef.offsetTop - navListRef.value.offsetHeight / 2,
      behavior: 'smooth',
    });
  } else if (
    direction === 'down' &&
    activeTabIndex.value <= tabs.value.length - 2
  ) {
    selectTab(tabs.value[activeTabIndex.value + 1].computedId);
    const tabRef = itemRefs[activeTabIndex.value];
    navListRef.value.scrollTo({
      top: tabRef.offsetTop - navListRef.value.offsetTop / 2,
      behavior: 'smooth',
    });
  }
}

onBeforeUpdate(() => {
  itemRefs = [];
});

watch(activeTabId, (val) => {
  emits('changed', val);
});
</script>

<style scoped lang="scss">
.ui-tabs {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &--horizontal {
    min-height: 10px;
    .ui-tabs__nav-shadow {
      min-height: 0px;
    }

    .ui-tabs__nav {
      min-height: 70px;
    }
  }

  &__btn {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    border-radius: 8px;

    &:hover {
      background: #3e9eea;
      svg {
        fill: #fff;
      }
    }

    svg {
      fill: #839aaf;
    }

    &.lock {
      background: #f4f6fd;
      cursor: default;

      svg {
        fill: #e1e5f0;
      }
    }

    &-up {
      svg {
        transform: scale(1, -1);
      }
    }

    @media (max-width: $md) {
      width: 30px;
      height: 30px;
    }
  }

  &__nav {
    position: relative;
    overflow: auto;
    align-self: stretch;
    align-items: center;
    flex-wrap: nowrap;
    list-style-type: none;
    width: max-content;
    margin: 0;
    display: flex;
    gap: 10px;
    border-radius: 6px;
    max-height: 50%;
    min-height: 250px;
    overflow: auto;

    &-shadow {
      position: relative;
      max-height: 50%;
      min-height: 250px;
      flex: 0;

      &:after {
        content: '';
        width: 100%;
        height: 25%;
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 100;
        transition: all 0.2s ease-in-out;
        pointer-events: none;
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 100%
        );
      }

      &:before {
        content: '';
        width: 100%;
        height: 25%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 100;
        transition: all 0.2s ease-in-out;
        pointer-events: none;
        background: linear-gradient(
          360deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 100%
        );
      }

      &.top:before {
        opacity: 0;
      }

      &.bottom:after {
        opacity: 0;
      }
    }

    &::-webkit-scrollbar {
      width: 0px;
    }

    &-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;

      @media (max-width: $lg) {
        margin-top: $mt-3;
        justify-content: center;
      }
    }

    &-title {
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.02em;
      color: #ffffff;
    }
  }

  &__tab {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0px 8px;
    width: 40px;
    height: 40px;
    min-height: 40px;
    background: transparent;
    transition: all 0.2s ease-in-out;
    text-align: center;
    color: #64686e;
    cursor: pointer;
    border-radius: 8px;
    border: 1px solid #7f97aa;
    font-size: 16px;
    font-weight: 700;
    line-height: 140%; /* 22.4px */
    letter-spacing: 0.48px;

    &.is-active {
      background: #3e9eea;
      border-color: #3e9eea;

      color: #ffffff;
    }

    &.is-disabled {
      color: #364e65;
      background: transparent;
    }

    @media (max-width: $md) {
      width: 30px;
      height: 30px;
      min-width: 30px;
      min-height: 30px;
    }
  }

  &--vertical {
    flex-direction: row;

    .ui-tabs__nav-wrapper {
      flex-direction: column;
      flex-shrink: 0;
      align-items: center;
    }

    .ui-tabs__nav {
      flex-direction: column;
    }
  }
}
ul {
  padding-inline-start: 0px;
}
</style>
